/** User-facing labels for disconnect workflow API status values. */

export const DISCONNECT_STATUS_LABELS = {
  none: "Disconnect Detected",
  flagged: "Disconnect Detected",
  pending_resolution: "Awaiting Primary Sponsor",
  resolved: "Pending until due",
  published: "Ready to publish",
  unresolved: "Unresolved",
} as const;

export type DisconnectWorkflowStatus = keyof typeof DISCONNECT_STATUS_LABELS;

export const DISCONNECT_AWAITING_RESOLUTION_LABEL =
  "Awaiting disconnect resolution";

/** Assessment card header when ESL accepted and hub publish is pending. */
export const DISCONNECT_RESOLVED_ASSESSMENT_LABEL = "Disconnect Resolved";

/** Action column / logs when disconnect is hub-published. */
export const DISCONNECT_HUB_PUBLISHED_LABEL = "Published";

/** ESL Disconnect Ongoing tab — sponsor awaiting primary. */
export const ESL_DISCONNECT_PENDING_LABEL = "Pending";

/** Disconnect Ongoing tab — primary recorded; pending until disconnect due. */
export const ESL_DISCONNECT_WAITING_REVIEW_LABEL =
  "Resolution recorded — Publish pending";

/** Hub account name / workflow tags (Published tab + Disconnect STATUS). */
export const HUB_DISCONNECT_ACCOUNT_UNRESOLVED_LABEL = "Unresolved disconnect";
export const HUB_DISCONNECT_ACCOUNT_RESOLVED_LABEL = "Resolved disconnect";

/** Formal + Team same-due combined publish (no disconnect workflow on account). */
export const HUB_COMBINED_PUBLISH_LABEL = "Combined Publish";
/** Formal + Team same-due combined publish with a disconnect workflow on account. */
export const HUB_COMBINED_DISCONNECT_LABEL = "Combined Disconnect";

export type HubDisconnectWorkflowAccountTag = {
  label: string;
  statusKey: "pending_resolution" | "resolved" | "unresolved";
  description: string | null;
};

/** Only pending / resolved / unresolved — no Published or Ready to publish pills. */
export const getHubDisconnectWorkflowAccountTag = (
  status?: string | null,
  hubPublished?: boolean
): HubDisconnectWorkflowAccountTag | null => {
  const key = normalizeDisconnectStatus(status);
  if (key === "unresolved") {
    return {
      label: HUB_DISCONNECT_ACCOUNT_UNRESOLVED_LABEL,
      statusKey: "unresolved",
      description: DISCONNECT_STATUS_DESCRIPTIONS.unresolved,
    };
  }
  if (key === "pending_resolution") {
    return {
      label: DISCONNECT_STATUS_LABELS.pending_resolution,
      statusKey: "pending_resolution",
      description: DISCONNECT_STATUS_DESCRIPTIONS.pending_resolution,
    };
  }
  if (key === "resolved") {
    return {
      label: HUB_DISCONNECT_ACCOUNT_RESOLVED_LABEL,
      statusKey: "resolved",
      description: DISCONNECT_STATUS_DESCRIPTIONS.resolved,
    };
  }
  if (key === "published" && hubPublished) {
    return null;
  }
  if (key === "published" && !hubPublished) {
    return {
      label: HUB_DISCONNECT_ACCOUNT_RESOLVED_LABEL,
      statusKey: "resolved",
      description: DISCONNECT_STATUS_DESCRIPTIONS.resolved,
    };
  }
  return null;
};

/** Hover descriptions for disconnect status tags in the hub. */
export const DISCONNECT_STATUS_DESCRIPTIONS = {
  pending_resolution:
    "Waiting for the Primary Sponsor to record a resolution. If none is recorded by the disconnect due date, the account is finalized as Unresolved.",
  resolved:
    "The Primary Sponsor has recorded a resolution. It publishes automatically the day after the disconnect due date, or sooner with Force Publish.",
  published:
    "The disconnect is final and the account level is published in the hub.",
  unresolved:
    "No resolution was recorded by the disconnect due date. The published median level stands.",
} as const;

export const DISCONNECT_ACTION_PILL_BASE =
  "inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold";

export const normalizeDisconnectStatus = (
  status?: string | null
): DisconnectWorkflowStatus | "unknown" => {
  const key = (status ?? "none").trim().toLowerCase();
  if (key in DISCONNECT_STATUS_LABELS) {
    return key as DisconnectWorkflowStatus;
  }
  return "unknown";
};

export const getDisconnectStatusLabel = (status?: string | null): string => {
  const key = normalizeDisconnectStatus(status);
  if (key === "unknown") {
    return DISCONNECT_STATUS_LABELS.none;
  }
  return DISCONNECT_STATUS_LABELS[key];
};

export const getDisconnectActionBadgeClass = (
  status?: string | null,
  hubPublished?: boolean
): string => {
  const key = normalizeDisconnectStatus(status);
  if (key === "pending_resolution") {
    return "bg-orange-50 text-orange-700 border border-orange-200";
  }
  if (key === "resolved") {
    return "bg-sky-50 text-sky-700 border border-sky-200";
  }
  if (key === "unresolved") {
    return "bg-red-50 text-red-700 border border-red-200";
  }
  if (key === "published") {
    return hubPublished
      ? "bg-green-50 text-green-700 border border-green-200"
      : "bg-yellow-50 text-yellow-800 border border-yellow-300";
  }
  return "bg-gray-100 text-gray-600 border border-gray-200";
};

/** Hover description for a disconnect status tag; null when there is none. */
export const getDisconnectStatusDescription = (
  status?: string | null,
  hubPublished?: boolean
): string | null => {
  const key = normalizeDisconnectStatus(status);
  if (key === "published" && !hubPublished) {
    return DISCONNECT_STATUS_DESCRIPTIONS.resolved;
  }
  if (key in DISCONNECT_STATUS_DESCRIPTIONS) {
    return DISCONNECT_STATUS_DESCRIPTIONS[
      key as keyof typeof DISCONNECT_STATUS_DESCRIPTIONS
    ];
  }
  return null;
};

export const getDisconnectActionPillBadgeClass = (
  status?: string | null,
  hubPublished?: boolean
): string =>
  `${DISCONNECT_ACTION_PILL_BASE} ${getDisconnectActionBadgeClass(status, hubPublished)}`;

/** Full pill badge class — delegates to hub-aware action styling. */
export const getDisconnectStatusBadgeClass = (
  status?: string | null,
  hubPublished?: boolean
): string => getDisconnectActionPillBadgeClass(status, hubPublished);

export const getDisconnectActionLabel = (
  status?: string | null,
  hubPublished?: boolean
): string => {
  const key = normalizeDisconnectStatus(status);
  if (key === "published" && hubPublished) {
    return DISCONNECT_HUB_PUBLISHED_LABEL;
  }
  return getDisconnectStatusLabel(status);
};

/** Alias for assessment hub action column labels. */
export const disconnectActionLabel = getDisconnectActionLabel;

/**
 * ESL Hub Disconnect Ongoing status labels (Pending / resolution recorded).
 * Logs (hub-published) still use Published.
 */
export const getEslDisconnectOngoingStatusLabel = (
  status?: string | null,
  hubPublished?: boolean
): string => {
  const key = normalizeDisconnectStatus(status);
  if (key === "pending_resolution") return ESL_DISCONNECT_PENDING_LABEL;
  if (key === "published" && hubPublished)
    return DISCONNECT_HUB_PUBLISHED_LABEL;
  if (key === "resolved" || key === "published")
    return ESL_DISCONNECT_WAITING_REVIEW_LABEL;
  return getDisconnectStatusLabel(status);
};
